// API service for communicating with the Verfolio backend
import { API_BASE_URL } from '../utils/env.js';
import { supabase } from '../lib/supabase.js';

// Get the current Supabase JWT token for authenticated requests.
// Retries once after a short delay to handle the race condition where the
// page loads immediately after signup before the session has been persisted.
async function getAuthHeaders() {
  let { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    await new Promise(r => setTimeout(r, 500));
    ({ data: { session } } = await supabase.auth.getSession());
  }

  const token = session?.access_token;
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

export class ApiService {
  static async get(endpoint) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, { headers });
    if (!response.ok) {
      throw new Error(`GET ${endpoint} failed: ${response.statusText}`);
    }
    return response.json();
  }

  static async post(endpoint, data) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`POST ${endpoint} failed: ${response.statusText}`);
    }
    return response.json();
  }

  static async put(endpoint, data) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`PUT ${endpoint} failed: ${response.statusText}`);
    }
    return response.json();
  }

  static async patch(endpoint, data) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`PATCH ${endpoint} failed: ${response.statusText}`);
    }
    return response.json();
  }

  static async delete(endpoint) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      throw new Error(`DELETE ${endpoint} failed: ${response.statusText}`);
    }
    if (response.status === 204) return { success: true };
    const text = await response.text();
    return text ? JSON.parse(text) : { success: true };
  }

  // Achievements
  static async getAchievements() {
    return this.get('achievements');
  }

  static async createAchievement(achievement) {
    return this.post('achievements', achievement);
  }

  static async updateAchievement(id, achievement) {
    return this.put(`achievements/${id}`, achievement);
  }

  static async deleteAchievement(id) {
    return this.delete(`achievements/${id}`);
  }

  // Pending logs ("Not an achievement yet?" placeholders)
  static async getPendingLogs() {
    return this.get('pending-logs');
  }

  static async createPendingLog(note) {
    return this.post('pending-logs', { note });
  }

  static async deletePendingLog(id) {
    return this.delete(`pending-logs/${id}`);
  }

  static async getAchievementsByPosition(userId, positionId, status = null) {
    let url = `achievements/position/${positionId}?userId=${userId}`;
    if (status) url += `&status=${status}`;
    return this.get(url);
  }

  static async getAchievementsGroupedByPosition() {
    return this.get('achievements/grouped/by-position');
  }

  // Endorsements / Witnesses
  static async addEndorsement(achievementId) {
    return this.post(`achievements/${achievementId}/endorse`, {});
  }

  static async addWitness(achievementId, witnessData) {
    return this.post(`achievements/${achievementId}/witness`, witnessData);
  }

  // Profile — no userId in URL; server resolves from JWT
  static async getProfile() {
    return this.get('profile');
  }

  static async createProfile(profileData) {
    return this.post('profile', profileData);
  }

  static async updateProfile(profileData) {
    return this.put('profile', profileData);
  }

  static async checkUserExists() {
    return this.get('profile/exists');
  }

  // Positions
  static async getUserPositions() {
    return this.get('positions');
  }

  static async getPosition(positionId) {
    return this.get(`positions/position/${positionId}`);
  }

  static async createPosition(positionData) {
    return this.post('positions', positionData);
  }

  static async updatePosition(positionId, positionData) {
    return this.put(`positions/${positionId}`, positionData);
  }

  static async deletePosition(positionId) {
    return this.delete(`positions/${positionId}`);
  }

  static async setCurrentPosition(positionId, userId) {
    return this.put(`positions/${positionId}/set-current/${userId}`, {});
  }

  static async updateResponsibilities(positionId, responsibilities) {
    return this.patch(`positions/${positionId}/responsibilities`, { responsibilities });
  }

  static async updatePositionField(positionId, fieldName, value) {
    return this.patch(`positions/${positionId}/field/${fieldName}`, { value });
  }

  // AI Enhancement
  static async improveAchievementWithAI(text, achievementType = 'Work Achievement', userRole = null) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/ai/improve-achievement`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text: text.trim(), achievementType, userRole }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `AI enhancement failed: ${response.statusText}`);
    }
    return response.json();
  }

  static async submitAIFeedback(originalText, aiSuggestion, feedback, achievementType) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/ai/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          originalText,
          aiSuggestion,
          feedback,
          achievementType,
          timestamp: new Date().toISOString(),
        }),
      });
      if (!response.ok) return { success: false };
      return response.json();
    } catch {
      return { success: false };
    }
  }

  // Toggle highlight
  static async toggleHighlight(achievementId, isHighlight) {
    return this.patch(`achievements/${achievementId}/highlight`, { is_highlight: isHighlight });
  }

  // Endorsements
  static async requestEndorsement(achievementId, managerEmail, managerName) {
    return this.post('endorsements/request', {
      achievement_id: achievementId,
      manager_email: managerEmail,
      manager_name: managerName,
    });
  }

  // Generic form submission (landing page waitlist — no auth needed)
  static async handleFormSubmission(url, data, options = {}) {
    try {
      const { method = 'POST', onSuccess, onError, onFinally } = options;
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (response.ok) {
        if (onSuccess) onSuccess(result);
        return { success: true, data: result };
      } else {
        if (onError) onError(result);
        return { success: false, error: result.message || 'Request failed' };
      }
    } catch (error) {
      const errorMessage = error.message || 'Network error';
      if (options.onError) options.onError({ message: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      if (options.onFinally) options.onFinally();
    }
  }
}

export default ApiService;
